import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { UserPlus, CircleNotch, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import { formatDate } from "./helpers";
import type { Employee } from "@/types";

export interface EmployeesSectionProps {
  companyId: string;
  employees: Employee[];
}

export function EmployeesSection({
  companyId,
  employees,
}: EmployeesSectionProps) {
  const queryClient = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState("");
  const [showInvite, setShowInvite] = useState(false);

  const inviteMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await fetch(`/api/companies/${companyId}/employees/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      await throwIfNotOk(res, "Greška pri pozivanju zaposlenog");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "companies", companyId, "employees"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "company", companyId],
      });
      toast.success("Zaposleni pozvan");
      setInviteEmail("");
      setShowInvite(false);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const res = await fetch(
        `/api/companies/${companyId}/employees/${employeeId}`,
        { method: "DELETE" }
      );
      await throwIfNotOk(res, "Greška pri uklanjanju zaposlenog");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "companies", companyId, "employees"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "company", companyId],
      });
      toast.success("Zaposleni uklonjen");
    },
  });

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Zaposleni ({employees.length})
        </h2>
        {!showInvite && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowInvite(true)}
          >
            <UserPlus className="size-3.5" />
            Pozovi
          </Button>
        )}
      </div>

      {showInvite && (
        <div className="border-b border-border/40 px-4 py-3">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="email@primjer.ba"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="text-sm"
            />
            <Button
              size="sm"
              onClick={() => inviteMutation.mutate(inviteEmail)}
              disabled={!inviteEmail || inviteMutation.isPending}
            >
              {inviteMutation.isPending ? (
                <CircleNotch className="size-3.5 animate-spin" />
              ) : (
                <PaperPlaneTilt className="size-3.5" />
              )}
              Pošalji
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowInvite(false);
                setInviteEmail("");
              }}
            >
              Otkaži
            </Button>
          </div>
        </div>
      )}

      {employees.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Nema zaposlenih. Pozovite prvog zaposlenog putem email adrese.
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-muted/40">
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Ime
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Email
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Datum
                  </th>
                  <th className="w-[40px] px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, i) => (
                  <tr
                    key={emp.id}
                    className={cn(
                      "transition-colors hover:bg-primary/[0.03]",
                      i !== employees.length - 1 &&
                        "border-b border-border/40"
                    )}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-bold text-muted-foreground">
                          {(emp.name || emp.inviteEmail)
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <span className="font-medium text-foreground">
                          {emp.name || "---"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {emp.email || emp.inviteEmail}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                          emp.inviteStatus === "accepted"
                            ? "bg-emerald-500/10 text-emerald-600"
                            : "bg-amber-500/10 text-amber-600"
                        )}
                      >
                        {emp.inviteStatus === "accepted" ? "Aktivan" : "Čeka"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {emp.joinedAt
                        ? formatDate(emp.joinedAt)
                        : formatDate(emp.createdAt)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground hover:text-destructive"
                        disabled={removeMutation.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `Ukloniti ${emp.name || emp.inviteEmail}?`
                            )
                          ) {
                            removeMutation.mutate(emp.id);
                          }
                        }}
                      >
                        <Trash className="size-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="grid gap-2 p-3 sm:hidden">
            {employees.map((emp) => (
              <div
                key={emp.id}
                className="rounded-lg border border-border/40 bg-muted/10 p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-bold text-muted-foreground">
                      {(emp.name || emp.inviteEmail).charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {emp.name || emp.inviteEmail}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase",
                        emp.inviteStatus === "accepted"
                          ? "bg-emerald-500/10 text-emerald-600"
                          : "bg-amber-500/10 text-amber-600"
                      )}
                    >
                      {emp.inviteStatus === "accepted" ? "Aktivan" : "Čeka"}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-muted-foreground hover:text-destructive"
                      disabled={removeMutation.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            `Ukloniti ${emp.name || emp.inviteEmail}?`
                          )
                        ) {
                          removeMutation.mutate(emp.id);
                        }
                      }}
                    >
                      <Trash className="size-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {emp.email || emp.inviteEmail}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
